import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const AlgebraicMultiplication = () => {
  const [expression1, setExpression1] = useState("2x + 3");
  const [expression2, setExpression2] = useState("x - 1");
  const [result, setResult] = useState("");
  const [steps, setSteps] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState("polynomial");
  const { toast } = useToast();

  // Polynomial multiplication examples
  const polynomialExamples = [
    { expr1: "2x + 3", expr2: "x - 1", desc: "Linear × Linear" },
    { expr1: "x² + 2x + 1", expr2: "x + 3", desc: "Quadratic × Linear" },
    { expr1: "x² - 4", expr2: "x + 2", desc: "Difference of Squares" },
    { expr1: "3x² + 2x - 1", expr2: "2x - 3", desc: "Quadratic × Linear" }
  ];

  // Distributive property examples
  const distributiveExamples = [
    { expr1: "3", expr2: "2x + 5", desc: "Scalar × Binomial" },
    { expr1: "x", expr2: "x² + 3x - 2", desc: "Monomial × Trinomial" },
    { expr1: "-2y", expr2: "3y² - y + 4", desc: "Negative Monomial" }
  ];

  // FOIL method examples
  const foilExamples = [
    { expr1: "x + 2", expr2: "x + 3", desc: "Both Positive" },
    { expr1: "x - 4", expr2: "x + 1", desc: "Mixed Signs" },
    { expr1: "2x + 1", expr2: "3x - 2", desc: "With Coefficients" }
  ];

  const parsePolynomial = (expr: string) => {
    // Simple polynomial parser for demonstration
    const terms = expr.replace(/\s/g, '').split(/(?=[+-])/);
    return terms.filter(term => term.length > 0);
  };

  const multiplyPolynomials = () => {
    const steps = [];
    steps.push(`(${expression1}) × (${expression2})`);
    
    if (selectedType === "foil" && expression1.includes('+') || expression1.includes('-')) {
      // FOIL method for binomials
      steps.push("Menggunakan metode FOIL:");
      steps.push("F: First terms");
      steps.push("O: Outer terms"); 
      steps.push("I: Inner terms");
      steps.push("L: Last terms");
    } else if (selectedType === "distributive") {
      steps.push("Menggunakan sifat distributif:");
      steps.push("Kalikan setiap suku di ekspresi pertama dengan setiap suku di ekspresi kedua");
    }

    // Simplified result calculation for demo
    const simpleResult = `Hasil: ${expression1} × ${expression2}`;
    steps.push(simpleResult);
    
    setSteps(steps);
    setResult(`(${expression1}) × (${expression2})`);
  };

  const loadExample = (example: { expr1: string; expr2: string; desc: string }) => {
    setExpression1(example.expr1);
    setExpression2(example.expr2);
    setResult("");
    setSteps([]);
  };

  const getExamples = () => {
    switch (selectedType) {
      case "distributive": return distributiveExamples;
      case "foil": return foilExamples;
      default: return polynomialExamples;
    }
  };

  const copyResult = async () => {
    if (!result || !steps.length) return;
    
    const resultText = `Hasil Perkalian Aljabar (${selectedType.toUpperCase()}):

${result}

Langkah-langkah:
${steps.map((step, index) => `${index + 1}. ${step}`).join('\n')}`;
    
    try {
      await navigator.clipboard.writeText(resultText);
      toast({
        title: "Berhasil!",
        description: "Hasil perhitungan telah disalin ke clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal menyalin hasil",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Perkalian Aljabar & Kalkulus</CardTitle>
          <CardDescription>
            Pelajari perkalian ekspresi aljabar dengan berbagai metode
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedType} onValueChange={setSelectedType}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="polynomial">Polinomial</TabsTrigger>
              <TabsTrigger value="distributive">Distributif</TabsTrigger>
              <TabsTrigger value="foil">FOIL</TabsTrigger>
            </TabsList>

            <TabsContent value="polynomial" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Perkalian Polinomial</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Polinomial Pertama</Label>
                      <Input
                        value={expression1}
                        onChange={(e) => setExpression1(e.target.value)}
                        placeholder="contoh: 2x + 3"
                      />
                    </div>
                    <div>
                      <Label>Polinomial Kedua</Label>
                      <Input
                        value={expression2}
                        onChange={(e) => setExpression2(e.target.value)}
                        placeholder="contoh: x - 1"
                      />
                    </div>
                  </div>

                  <Button onClick={multiplyPolynomials} className="w-full">
                    Kalikan Polinomial
                  </Button>

                  <div className="grid grid-cols-2 gap-2">
                    {polynomialExamples.map((example, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => loadExample(example)}
                        className="text-xs"
                      >
                        {example.desc}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="distributive" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Sifat Distributif</CardTitle>
                  <CardDescription>a(b + c) = ab + ac</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Faktor</Label>
                      <Input
                        value={expression1}
                        onChange={(e) => setExpression1(e.target.value)}
                        placeholder="contoh: 3x"
                      />
                    </div>
                    <div>
                      <Label>Ekspresi</Label>
                      <Input
                        value={expression2}
                        onChange={(e) => setExpression2(e.target.value)}
                        placeholder="contoh: 2x + 5"
                      />
                    </div>
                  </div>

                  <Button onClick={multiplyPolynomials} className="w-full">
                    Terapkan Distributif
                  </Button>

                  <div className="grid grid-cols-1 gap-2">
                    {distributiveExamples.map((example, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => loadExample(example)}
                        className="text-xs"
                      >
                        {example.expr1} × ({example.expr2}) - {example.desc}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="foil" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Metode FOIL</CardTitle>
                  <CardDescription>First, Outer, Inner, Last</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Binomial Pertama</Label>
                      <Input
                        value={expression1}
                        onChange={(e) => setExpression1(e.target.value)}
                        placeholder="contoh: x + 2"
                      />
                    </div>
                    <div>
                      <Label>Binomial Kedua</Label>
                      <Input
                        value={expression2}
                        onChange={(e) => setExpression2(e.target.value)}
                        placeholder="contoh: x + 3"
                      />
                    </div>
                  </div>

                  <Button onClick={multiplyPolynomials} className="w-full">
                    Gunakan FOIL
                  </Button>

                  <div className="space-y-2">
                    <div className="text-sm font-semibold">Metode FOIL:</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-red-50 rounded">🔴 First: Suku pertama × Suku pertama</div>
                      <div className="p-2 bg-blue-50 rounded">🔵 Outer: Suku luar × Suku luar</div>
                      <div className="p-2 bg-green-50 rounded">🟢 Inner: Suku dalam × Suku dalam</div>
                      <div className="p-2 bg-yellow-50 rounded">🟡 Last: Suku terakhir × Suku terakhir</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    {foilExamples.map((example, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => loadExample(example)}
                        className="text-xs"
                      >
                        ({example.expr1}) × ({example.expr2}) - {example.desc}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {result && (
            <Card className="mt-6 bg-gradient-to-r from-primary/10 to-secondary/10">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Hasil Perkalian
                  <Button
                    onClick={copyResult}
                    variant="outline"
                    size="sm"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Salin
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-center mb-4">
                  {result}
                </div>
                
                {steps.length > 0 && (
                  <div className="space-y-2">
                    <div className="font-semibold">Langkah-langkah:</div>
                    {steps.map((step, index) => (
                      <div key={index} className="text-sm font-mono p-2 bg-muted rounded">
                        {index + 1}. {step}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Reference */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Referensi Cepat</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="font-semibold">Identitas Aljabar:</div>
                  <div>(a + b)² = a² + 2ab + b²</div>
                  <div>(a - b)² = a² - 2ab + b²</div>
                  <div>(a + b)(a - b) = a² - b²</div>
                </div>
                <div>
                  <div className="font-semibold">Distributif:</div>
                  <div>a(b + c) = ab + ac</div>
                  <div>a(b - c) = ab - ac</div>
                  <div>(a + b)c = ac + bc</div>
                </div>
                <div>
                  <div className="font-semibold">Pangkat:</div>
                  <div>x^m × x^n = x^(m+n)</div>
                  <div>(x^m)^n = x^(mn)</div>
                  <div>(xy)^n = x^n × y^n</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};

export default AlgebraicMultiplication;